import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./fileTree.html";
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
// Icon choices: chevrondown + opened_folder for expanded dirs, chevronright + open_folder for
// collapsed dirs, and page for files. All are provided by the AppExchange design system.
const CHEVRON_DOWN_ICON = 'chevrondown';
const CHEVRON_RIGHT_ICON = 'chevronright';
const CLOSED_FOLDER_ICON = 'open_folder';
const OPENED_FOLDER_ICON = 'opened_folder';
const FILE_ICON = 'page';
const EXT_ICON_MAP = {
  yaml: FILE_ICON,
  md: FILE_ICON,
  py: FILE_ICON,
  soql: FILE_ICON,
  js: FILE_ICON,
  json: FILE_ICON,
  html: FILE_ICON,
  cls: FILE_ICON,
  mmd: FILE_ICON,
  xml: FILE_ICON,
  cjs: FILE_ICON,
  css: FILE_ICON,
  example: FILE_ICON,
  ts: FILE_ICON,
  sh: FILE_ICON
};
function extToIcon(ext) {
  return ext && EXT_ICON_MAP[ext] || FILE_ICON;
}
class FileTree extends LightningElement {
  constructor(...args) {
    super(...args);
    this._nodes = [];
    // Keep the backing field initialized so the ES6 client build registers it with LWC's
    // reactivity membrane. Without runtime-emitted field initialization, a parent prop update
    // changes this value but does not rerender the rows; the next click then renders that stale
    // value and leaves the highlight exactly one selection behind the viewer.
    // DO NOT drop this initializer trusting a green Jest suite: the Jest transform keeps class
    // fields reactive regardless of initialization, so no unit test can fail when this regresses.
    // The production-only break shows up on keyboard activation of an already-focused row (see
    // `activateRow`), where `_selectedPath` is the sole reactive trigger — guard it via e2e.
    this._selectedPath = '';
    // Initialized so client-build class-field reactivity tracks roving-tabindex changes.
    this.activePath = '';
    this.labels = void 0;
    // True when the parent is filtering the tree by a search query. While filtering, dirs default to
    // EXPANDED so matches nested inside folders stay reachable; otherwise dirs default to COLLAPSED.
    this.filtering = false;
    // Explicit per-dir expand/collapse toggles set by the user (path -> expanded). A dir absent from
    // this map has NOT been toggled and falls back to the default (see `filtering`) in the walk below.
    // Reactive object reassignment triggers re-render in LWC.
    this.expandedDirs = {};
  }
  get nodes() {
    return this._nodes;
  }
  set nodes(value) {
    this._nodes = Array.isArray(value) ? value : [];
    this.expandedDirs = {};
  }
  get selectedPath() {
    return this._selectedPath;
  }
  set selectedPath(value) {
    // Normalize BEFORE comparing: `_selectedPath` is always normalized to '', so comparing the
    // raw optional `value` would treat a reapplied `undefined` as a change (undefined !== '') and
    // reset `activePath` — yanking the roving tab stop back to the first row while focus sits on
    // another row. Compare and assign the normalized value so a no-op reapply stays a no-op.
    const nextPath = value ?? '';
    if (nextPath !== this._selectedPath || !this.activePath) {
      this.activePath = nextPath;
    }
    this._selectedPath = nextPath;
  }
  // True when `dirPath` is an ancestor directory of the currently selected file.
  isAncestorOfSelected(dirPath) {
    return !!this.selectedPath && this.selectedPath.startsWith(`${dirPath}/`);
  }

  // The dir's current expanded state: the user's explicit toggle if any, else the default
  // (filtering, or an ancestor of the selection). Shared by `rows` and the click toggle so
  // toggling always flips the state actually on screen, not just the explicit-toggle map.
  isDirExpanded(dirPath) {
    return this.expandedDirs[dirPath] ?? (this.filtering || this.isAncestorOfSelected(dirPath));
  }
  folderActionLabel(isExpanded, folderName) {
    const labels = i18nStore.getState().i18n.skills;
    const formatter = isExpanded ? labels.packageFilesCollapseFolder : labels.packageFilesExpandFolder;
    return formatter({
      FOLDER: folderName
    });
  }

  // Recursion happens here, in a plain-TS depth-first walk of the nested `nodes`. The
  // template below renders the resulting flat list with ONE `for:each` — LWC forbids a
  // template from referencing its own tag, so this component cannot recurse in the markup.
  // Dirs can now be collapsed; walk skips children of collapsed dirs.
  get rows() {
    const out = [];
    const walk = (list, depth) => {
      for (const n of list ?? []) {
        const isFile = n.type === 'file';
        const isDir = n.type === 'dir';
        const isExpanded = isDir && this.isDirExpanded(n.path);
        const isSelected = isFile && this.selectedPath === n.path;
        out.push({
          path: n.path,
          name: isDir ? `${n.name}/` : n.name,
          // Append '/' to folder names
          isFile,
          isDir,
          depth,
          ext: n.ext,
          icon: isFile ? extToIcon(n.ext) : isExpanded ? OPENED_FOLDER_ICON : CLOSED_FOLDER_ICON,
          indentStyle: `padding-left: ${depth * 32}px`,
          hasLeftBorder: depth >= 1,
          isSelected,
          isExpanded,
          chevronIcon: isExpanded ? CHEVRON_DOWN_ICON : CHEVRON_RIGHT_ICON,
          ariaExpanded: isDir ? isExpanded ? 'true' : 'false' : undefined,
          ariaCurrent: isSelected ? 'true' : undefined,
          ariaSelected: isSelected ? 'true' : 'false',
          ariaLevel: depth + 1,
          ariaLabel: isDir ? this.folderActionLabel(isExpanded, n.name) : undefined,
          tabIndex: '-1'
        });
        // Only walk children if the dir is expanded
        if (isDir && isExpanded) {
          walk(n.children, depth + 1);
        }
      }
    };
    walk(this.nodes, 0);
    const activePath = out.some(row => row.path === this.activePath) ? this.activePath : out.find(row => row.isSelected)?.path ?? out[0]?.path;
    for (const row of out) {
      row.tabIndex = row.path === activePath ? '0' : '-1';
    }
    return out;
  }
  get treeLabel() {
    return this.labels?.packageFilesTreeLabel ?? '';
  }
  handleRowClick(event) {
    const path = event.currentTarget.dataset.path;
    if (!path) {
      return;
    }
    this.activePath = path;
    this.activateRow(path);
  }
  handleRowFocus(event) {
    this.activePath = event.currentTarget.dataset.path ?? '';
  }
  handleTreeKeyDown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    const target = event.composedPath().find(candidate => candidate instanceof HTMLElement && candidate.dataset.path);
    const path = target?.dataset.path;
    const rows = this.rows;
    const index = rows.findIndex(row => row.path === path);
    if (index < 0) {
      return;
    }
    const row = rows[index];
    let nextPath;
    switch (event.key) {
      case 'ArrowDown':
        nextPath = rows[Math.min(index + 1, rows.length - 1)]?.path;
        break;
      case 'ArrowUp':
        nextPath = rows[Math.max(index - 1, 0)]?.path;
        break;
      case 'Home':
        nextPath = rows[0]?.path;
        break;
      case 'End':
        nextPath = rows[rows.length - 1]?.path;
        break;
      case 'ArrowRight':
        if (!row.isDir) {
          return;
        }
        if (!row.isExpanded) {
          this.setDirExpanded(row.path, true);
          nextPath = row.path;
        } else if (rows[index + 1]?.depth > row.depth) {
          nextPath = rows[index + 1].path;
        }
        break;
      case 'ArrowLeft':
        if (row.isDir && row.isExpanded) {
          this.setDirExpanded(row.path, false);
          nextPath = row.path;
          break;
        }
        for (let parentIndex = index - 1; parentIndex >= 0; parentIndex -= 1) {
          if (rows[parentIndex].depth === row.depth - 1) {
            nextPath = rows[parentIndex].path;
            break;
          }
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.activateRow(row.path);
        return;
      default:
        return;
    }
    event.preventDefault();
    if (nextPath) {
      this.focusRow(nextPath);
    }
  }
  setDirExpanded(path, isExpanded) {
    // Toggle the CURRENT effective state (not just the explicit map) — otherwise clicking a dir
    // that's expanded by default (filtering, or an ancestor of the selection) would no-op.
    // LWC reactivity requires reassignment of the object.
    this.expandedDirs = {
      ...this.expandedDirs,
      [path]: isExpanded
    };
  }
  activateRow(path) {
    const row = this.rows.find(candidate => candidate.path === path);
    if (!row) {
      return;
    }
    if (row.isFile) {
      // Reflect the user's choice in this pane immediately. The parent remains the source of
      // truth and will pass the same path back down, but waiting for that multi-component
      // round trip made the highlight susceptible to rendering one selection behind.
      this._selectedPath = path;
      this.dispatchEvent(new CustomEvent('select', {
        detail: {
          path
        }
      }));
      this.dispatchTreeAnalytics('fileTreeFile', path);
      return;
    }
    this.setDirExpanded(path, !row.isExpanded);
    this.dispatchTreeAnalytics('fileTreeDir', path);
  }
  focusRow(path) {
    this.activePath = path;
    Promise.resolve().then(() => {
      const rows = this.template?.querySelectorAll('[role="treeitem"][data-path]') ?? [];
      Array.from(rows).find(row => row.dataset.path === path)?.focus();
    });
  }

  // Additive analytics ping alongside the native select/toggle behavior above — same 'analytics'
  // CustomEvent shape used across wds/* (see ace/filterSearch, modules/analytics/analyticsHelper).
  dispatchTreeAnalytics(elementType, path) {
    this.dispatchEvent(new CustomEvent('analytics', {
      detail: {
        eventType: 'click',
        elementType,
        value: path
      },
      composed: true,
      bubbles: true,
      cancelable: true
    }));
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(FileTree, {
  publicProps: {
    nodes: {
      config: 3
    },
    selectedPath: {
      config: 3
    },
    filtering: {
      config: 0
    }
  },
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.skills
        };
      }
    }
  },
  fields: ["_nodes", "_selectedPath", "activePath", "expandedDirs"]
});
const __lwc_component_class_internal = _registerComponent(FileTree, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;